import { ArrowDownTrayIcon, TrashIcon, StarIcon, PlayIcon } from '@heroicons/react/24/solid';
import { ExclamationCircleIcon, StarIcon as StarOutline } from '@heroicons/react/24/outline';
import { ShieldCheckIcon } from '@heroicons/react/24/solid';
import SocialShareButton from './social-share-button';
import React, { useEffect, useRef, useState } from 'react';
import { SwapResponse } from '../../api/types';
import type { UseMutationResult } from '@tanstack/react-query/src/types';
import { useUpdateSwap } from '../../hooks/api-hooks';
import { handleDownloadSwap, timeAgo } from '../../utils';
import LoadingSpinner from '../../shared/spinner';

interface SwapCardProps {
  swap: SwapResponse;
  handleOpenModal: (url: string, targetUrl: string | null, contentType: string, swap: SwapResponse) => void;
  handleDeleteSwap: (swapId: string) => void;
  deleteSwapMutations: UseMutationResult<void, Error, { swapId: string }>;
}

const SwapCard = ({ swap, handleDeleteSwap, deleteSwapMutations, handleOpenModal }: SwapCardProps) => {
  const updateSwapMutation = useUpdateSwap();
  const videoRef = useRef<HTMLVideoElement>(null);
  const isOriginalDeleted = swap.targetFaceDeletedAt;

  useEffect(() => {
    // Force load first frame on iOS
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [swap.swappedFaceUrl]);

  // Downloading files
  const [isDownloadingSwap, setIsDownloadingSwap] = useState(false);

  const handleFavoriteToggle = () => {
    // Optimistic update for isFavorite
    updateSwapMutation.mutate(
      {
        updateSwapBody: {
          isFavorite: !swap.isFavorite,
        },
        swapId: swap.id,
      },
      {
        onSuccess: (swapResponse) => {
          swap.isFavorite = swapResponse.isFavorite;
        },
      }
    );
  };

  /**
   * Handle download swap state
   *
   * @param swapId
   */
  const handleDownloadSwapState = async (swapId: string | null) => {
    setIsDownloadingSwap(true)
    await handleDownloadSwap(swapId);
    setIsDownloadingSwap(false)
  }

  return (
    <li className='bg-white shadow rounded-lg relative group'>
      {/* Favorite button */}
      <button
        onClick={handleFavoriteToggle}
        className='absolute top-2 right-2 z-10 p-2 rounded-full bg-black/20 backdrop-blur-sm
        transition-colors hover:bg-black/30 active:bg-black/40'
        aria-label={swap.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      >
        {swap.isFavorite ? (
          <StarIcon className='h-5 w-5 text-yellow-400' />
        ) : (
          <StarOutline className='h-5 w-5 text-white' />
        )}
      </button>
      <div className='relative aspect-square'>
        {swap.swappedFaceUrl || swap.targetFaceUrl ? (
          (swap.mediaType === 'video' ? (
            <div
              onClick={() => handleOpenModal(
                swap.swappedFaceUrl || swap.targetFaceUrl || '',
                swap.targetFaceUrl,
                swap.targetFaceFile?.contentType || '',
                swap
              )
              }
              className={`relative w-full h-full cursor-pointer`}
            >
              <video
                ref={videoRef}
                playsInline
                muted
                preload="metadata"
                webkit-playsinline="true"
                className={`h-full w-full object-cover transition-opacity ${
                  swap.status === 'completed' ? 'hover:opacity-75' : ''
                } ${(swap.status === 'processing' || swap.status === 'created') ? 'opacity-50' : ''}`}
              >
                <source src={swap.swappedFaceUrl || swap.targetFaceUrl || ''}
                        type={swap.targetFaceFile?.contentType || ''} />
              </video>
              {/* Play button overlay for videos */}
              {swap.status === 'completed' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="rounded-full bg-black/30 p-4 backdrop-blur-sm
                    transition-all duration-200 group-hover:bg-black/40 group-hover:scale-110">
                    <PlayIcon className="h-8 w-8 text-white" />
                  </div>
                </div>
              )}
              {/* ETA overlay for processing videos */}
              {(swap.status === 'processing' || swap.status === 'created') && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
                  <div className="relative group/eta cursor-help">
                    <div className="px-4 py-2 text-sm font-medium text-white bg-black/50 rounded-full backdrop-blur-sm">
                      ETA: 10-20 min
                    </div>
                    <div className="opacity-0 group-hover/eta:opacity-100 transition-opacity absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-2 w-64 text-center text-xs text-white bg-gray-900 rounded-lg">
                      Videos take approximately 10-20 minutes to process during beta. You can turn on notifications and we'll let you know when it's ready!
                      <div className="absolute w-3 h-3 bg-gray-900 transform rotate-45 left-1/2 -translate-x-1/2 -bottom-1.5"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <img
              src={swap.swappedFaceUrl || swap.targetFaceUrl || ''}
              onClick={() => handleOpenModal(
                swap.swappedFaceUrl || swap.targetFaceUrl || '',
                swap.targetFaceUrl,
                swap.targetFaceFile?.contentType || '',
                swap
              )}
              alt={`Swap ${swap.id}`}
              className={`h-full w-full object-cover transition-opacity ${
                swap.status === 'completed' ? 'hover:opacity-75' : ''
              } ${(swap.status === 'processing' || swap.status === 'created') ? 'opacity-50' : ''}`}
            />
          ))
        ) : null}

        {(swap.status === 'processing' || swap.status === 'created') && (
          <div className='absolute inset-0 flex items-center justify-center'>
            <svg
              className='mx-auto h-12 w-12 text-indigo-500 animate-spin'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              />
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              />
            </svg>
          </div>
        )}

        {/* Error Message for Failed Status */}
        {swap.status === 'failed' && (
          <>
            <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center">
              <div className="text-center p-4">
                <ExclamationCircleIcon className="mx-auto h-12 w-12 text-white mb-2" />
                <div className="text-sm font-medium text-white">Swap Failed</div>
              </div>
            </div>
            {swap.failureReason && (
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-start p-3 rounded-md bg-red-50 border border-red-200">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-800 ml-2">
                    {swap.failureReason}
                  </p>
                </div>
              </div>
            )}
          </>
        )}

      </div>
      <div className='p-4'>
        <div className='flex'>
          <p className='grow'>
          <span
            className={`inline-flex items-center rounded-md ${getBGStatusColor(
              swap.status,
            )} px-2 py-1 text-xs font-medium ${getTextStatusColor(
              swap.status,
            )}`}
          >
            {getTextFromStatus(swap.status)}
          </span>
          </p>
          <p className='text-sm'>
            {timeAgo(swap.createdAt)}
          </p>
        </div>
        <div className='mt-4 flex justify-between'>
          <div className='flex items-center gap-2'>
            <button
              disabled={swap.status === 'processing' || swap.status === 'created'}
              className={`flex-1 inline-flex items-center justify-center rounded-md px-2.5 py-1.5 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                isDownloadingSwap
                  ? 'bg-indigo-400 cursor-not-allowed text-white'
                  : swap.status === 'processing' || swap.status === 'created'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 text-white'
              }`}
              onClick={async (e) => {
                if (swap.status === 'processing' || swap.status === 'created') return;
                await handleDownloadSwapState(swap.id);
              }}
            >
              {isDownloadingSwap ? (
                <>
                  <LoadingSpinner className='w-5 h-5' />
                </>
              ) : (
                <ArrowDownTrayIcon className={`h-5 w-5 ${
                  swap.status === 'processing' || swap.status === 'created'
                    ? 'text-gray-500'
                    : ''
                }`} />
              )}
            </button>

            <SocialShareButton url={swap.swappedFaceUrl || swap.targetFaceUrl || ''} />

            <div className='left-2'>
              {isOriginalDeleted ? (
                <ShieldCheckIcon className='h-6 w-6 text-green-500' />
              ) : <ShieldCheckIcon className='h-6 w-6 text-indigo-500' />}
            </div>
          </div>
          <button
            onClick={() => handleDeleteSwap(swap.id)}
            disabled={deleteSwapMutations.isPending}
            className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium text-gray-300 hover:text-gray-400'
          >
            {deleteSwapMutations.isPending &&
            swap.id === deleteSwapMutations.variables.swapId ? (
              <svg
                className='animate-spin h-5 w-5 text-gray-300'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                />
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                />
              </svg>
            ) : (
              <TrashIcon className='h-5 w-5' />
            )}
          </button>
        </div>
      </div>
    </li>
  );
}

const getBGStatusColor = (status: string) => {
  switch (status) {
    case 'created':
      return 'bg-blue-100'; // Changed to blue to indicate "pending/waiting"
    case 'processing':
      return 'bg-purple-100'; // Changed to purple to show "in progress/active"
    case 'completed':
      return 'bg-green-100'; // Keep green for success
    case 'failed':
      return 'bg-red-100'; // Keep red for failure
    default:
      return '';
  }
};

const getTextStatusColor = (status: string) => {
  switch (status) {
    case 'created':
      return 'text-blue-700';
    case 'processing':
      return 'text-purple-700';
    case 'completed':
      return 'text-green-700';
    case 'failed':
      return 'text-red-700';
    default:
      return '';
  }
};

const getTextFromStatus = (status: string) => {
  switch (status) {
    case 'created':
      return 'pending';
    case 'processing':
      return 'anonymizing';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    default:
      return '';
  }
}

export default SwapCard;